// Specialized parsing of a JWT with launch control return params specified
import {type ParserT, Parser, string, record, mapStatic, nullable, any} from '@freckle/parser'
import {ajaxJsonCall} from '@freckle/student-entities/ts/common/helpers/ajax'
import CApiHelper from '@freckle/student-entities/ts/common/helpers/common-api-helper'
import {useAsync} from '@freckle/student-entities/ts/common/hooks/use-async/index'
import {type CompletionDetail} from '@freckle/student/ts/common/helpers/session-completion-detail'

type JwtActivityLaunchDeepLink = {
  returnAppCode: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  returnDeepLink?: any
}

type Jwt = {
  homeuri: string
  deeplink: JwtActivityLaunchDeepLink
}

export type LaunchControlReturn = {
  homeUri: string
  returnApp: {
    code: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deepLink?: any
  }
}

export function jsonEncodedString<T>(parser: ParserT<T>): ParserT<T> {
  return {
    type: 'parser',
    expected: 'a json encoded string',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parse: (x: any, path: any) => {
      if (typeof x === 'string') {
        try {
          const parsed = JSON.parse(x)
          return parser.parse(parsed, path)
        } catch (_e) {
          return Parser.fail({expected: 'a json encoded string', got: x})
        }
      } else {
        return Parser.fail({expected: 'string', got: x})
      }
    }
  }
}

const parseJwt: ParserT<Jwt> = record({
  homeuri: string(),
  deeplink: jsonEncodedString(
    record({
      returnAppCode: string(),
      returnDeepLink: nullable(any())
    })
  )
})

const parse: ParserT<LaunchControlReturn> = mapStatic<Jwt, LaunchControlReturn>(parseJwt, jwt => ({
  homeUri: jwt.homeuri,
  returnApp: {
    code: jwt.deeplink.returnAppCode,
    deepLink: jwt.deeplink.returnDeepLink
  }
}))

export const parseLaunchControlReturn = Parser.mkRun<LaunchControlReturn>(parse)

const fetchLaunchControlReturn = (): Promise<LaunchControlReturn> =>
  ajaxJsonCall({
    url: CApiHelper.fancyPaths.v3.renaissance.jwt(),
    method: 'GET'
  }).then(parseLaunchControlReturn)

export const useLaunchControlReturn = ({enabled}: {enabled: boolean}) =>
  useAsync(() => (enabled ? fetchLaunchControlReturn() : Promise.resolve(null)), [enabled])

export const safeMergeCompletionDetail = (
  deepLink: unknown,
  completionDetail: CompletionDetail
): {completionDetail: CompletionDetail} => {
  if (typeof deepLink === 'object') {
    return {...deepLink, completionDetail}
  }

  return {completionDetail}
}
